import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    OnInit,
    ViewEncapsulation,
    computed,
    inject,
    input,
    output,
    signal,
} from "@angular/core"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { Router } from "@angular/router"
import { MatTableModule } from "@angular/material/table"
import { MatSortModule, Sort } from "@angular/material/sort"
import { MatTooltip } from "@angular/material/tooltip"
import { MatMenuModule } from "@angular/material/menu"
import { MatCheckboxModule } from "@angular/material/checkbox"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatDialog } from "@angular/material/dialog"
import { MatIcon } from "@angular/material/icon"
import { CdkDrag, CdkDragMove } from "@angular/cdk/drag-drop"
import { Subject, debounceTime } from "rxjs"
import {
    Column,
    ColumnFilterMeta,
    FileContent,
    FileContentFactory,
    ModifyLandlordData,
    TableLazyLoadEvent,
    applicationStateNames,
} from "../../models"
import { ContractsDialogComponent } from "./contracts-dialog.component"
import { ContextService } from "../../services"
import {
    MapModalComponent,
    FileModalComponent,
    HistoryComponent,
    ExpandModalComponent,
    SuperiorEvaluationModalComponent,
    LandlordDataModalComponent,
    FileInputComponent,
} from ".."
import { LangsDistinctPipe, StripHtmlPipe } from "../../pipes"

interface StoredState {
    first: number
    rows: number
    sortField: string
    sortOrder: number
    global: string
    filters: Record<string, unknown>
    subFields: Record<string, string>
}

const ACTIONS_COLUMN = "__actions"
const MIN_COL_PX = 128
const MAX_COL_PX = 640
const DEFAULT_COL_PX: Record<string, number> = { text: 256, langs: 256 }

const ACTION_TYPES = new Set(["file", "map", "modal", "kngEvaluation", "landlordData", "link"])

const ACTION_ICONS: Record<string, string> = {
    link: "link",
    file: "image",
    map: "place",
    modal: "sell",
    kngEvaluation: "assignment_ind",
    landlordData: "real_estate_agent",
}

@Component({
    selector: "valben-table",
    templateUrl: "./table.component.html",
    styleUrls: ["./table.component.css"],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        MatTableModule,
        MatSortModule,
        MatTooltip,
        MatMenuModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        MatIcon,
        CdkDrag,
        ExpandModalComponent,
        MapModalComponent,
        FileModalComponent,
        HistoryComponent,
        SuperiorEvaluationModalComponent,
        LandlordDataModalComponent,
        LangsDistinctPipe,
        StripHtmlPipe,
    ],
})
export class TableComponent implements OnInit, AfterViewInit {
    readonly entityType = input<string>("")
    readonly showHistory = input<boolean>(true)
    readonly showEdit = input<boolean>(true)
    readonly editText = input<string>("Uredi")
    readonly addLabel = input<string>("")
    readonly values = input<any[]>([])
    readonly columns = input<Column[]>([])
    readonly refresh = input<() => void>()
    readonly paginator = input<boolean>(true)
    readonly showKngContracts = input<boolean>(false)
    readonly lazy = input<boolean>(false)
    readonly totalRecords = input<number>(0)
    readonly first = input<number>(0)
    readonly rows = input<number>(10)
    readonly rowsPerPageOptions = input<number[]>([10, 25, 50])
    readonly loading = input<boolean>(false)

    readonly editEvent = output<string>()
    readonly addEvent = output<void>()
    readonly landlordDataSave = output<{ masterId: string, version: number, data: ModifyLandlordData }>()
    readonly lazyLoad = output<TableLazyLoadEvent>()

    // The table's view state (page, rows, sort, column filters, search term) is remembered per table so tables
    // don't clash. It has to survive the edit/add round-trip, which destroys and recreates this table via
    // *ngSwitchCase, but not survive leaving the section - see ngOnInit.
    private static readonly _statePrefix = "valben-table::"
    private static _lastRoute: string | null = null

    static resetPageOffset(): void {
        TableComponent._clearStoredState()
    }

    private static _clearStoredState(): void {
        Object.keys(sessionStorage)
            .filter(key => key.startsWith(TableComponent._statePrefix))
            .forEach(key => sessionStorage.removeItem(key))
    }

    readonly sortField = signal<string>("createdDate")
    readonly sortDir = signal<"asc" | "desc">("desc")
    readonly pageIndex = signal<number>(0)
    readonly pageSize = signal<number>(10)
    readonly globalFilter = signal<string>("")
    readonly filters = signal<Record<string, unknown>>({})
    readonly allCats = signal<string[]>([])
    readonly allLocs = signal<string[]>([])
    readonly allStates: string[] = Object.values(applicationStateNames)

    // Mirrors col.subField so OnPush and the computeds below react to a language change - see setSubField.
    private readonly _subFields = signal<Record<string, string>>({})

    private readonly _widths = signal<Record<string, number>>({})
    private _dragStartPx = 0

    readonly skeletonType = "__skeleton"
    readonly actionsColumn = ACTIONS_COLUMN

    readonly orderedColumns = computed<Column[]>(() => {
        const cols = [...this.columns()]
        const surnameIdx = cols.findIndex(col => col.field === "surname")
        const statusIdx = cols.findIndex(col => col.field === "stateLabel")

        if (surnameIdx !== -1 && statusIdx !== -1 && statusIdx !== surnameIdx + 1) {
            const [statusCol] = cols.splice(statusIdx, 1)
            cols.splice(surnameIdx + 1, 0, statusCol)
        }
        return cols
    })

    readonly actionColumns = computed<Column[]>(() =>
        this.orderedColumns().filter(col => ACTION_TYPES.has(col.bodyType)))

    readonly actionFilterColumns = computed<Column[]>(() =>
        this.actionColumns().filter(col => !!col.filterType))

    private readonly _pairedAway = computed<Set<string>>(() =>
        new Set(this.orderedColumns().map(col => col.pairWith).filter((field): field is string => !!field)))

    readonly bodyColumns = computed<Column[]>(() =>
        this.orderedColumns().filter(col => !ACTION_TYPES.has(col.bodyType) && !this._pairedAway().has(col.field)))

    readonly hasActions = computed<boolean>(() =>
        this.showEdit() || this.showHistory() || this.showKngContracts() || this.actionColumns().length > 0)

    readonly displayedColumns = computed<string[]>(() => [
        ...this.bodyColumns().map(col => col.field),
        ...(this.hasActions() ? [ACTIONS_COLUMN] : []),
    ])

    pairOf(col: Column): Column | undefined {
        return col.pairWith ? this.orderedColumns().find(other => other.field === col.pairWith) : undefined
    }

    iconFor(col: Column): string {
        if (col.icon) return col.icon
        if (col.bodyType === "modal" && col.filterType === "locs") return "place"
        return ACTION_ICONS[col.bodyType] ?? "open_in_new"
    }

    isSortedCol(col: Column): boolean {
        return !!col.sortable && this.sortField() === this.sortKey(col)
    }

    thSorted(col: Column): boolean {
        const pair = this.pairOf(col)
        return this.isSortedCol(col) || (!!pair && this.isSortedCol(pair))
    }

    thFiltered(col: Column): boolean {
        const pair = this.pairOf(col)
        return this.hasFilter(col) || (!!pair && this.hasFilter(pair))
    }

    // Server-driven tables get their rows straight from the page; the rest filter, sort and paginate here,
    // which is what p-table used to do for them.
    private readonly _view = computed<{ rows: any[], total: number, pageIndex: number }>(() => {
        if (this.lazy()) {
            return { rows: this.values() ?? [], total: this.totalRecords(), pageIndex: this.pageIndex() }
        }

        const matched = (this.values() ?? []).filter(row => this._matchesGlobal(row) && this._matchesColumns(row))
        const sorted = this._sortRows(matched)

        if (!this.paginator()) {
            return { rows: sorted, total: sorted.length, pageIndex: 0 }
        }

        // A shrinking result set can strand the view past the last page; fall back to the first.
        const size = this.pageSize()
        const index = this.pageIndex() > 0 && this.pageIndex() * size >= sorted.length ? 0 : this.pageIndex()
        const start = index * size
        return { rows: sorted.slice(start, start + size), total: sorted.length, pageIndex: index }
    })

    readonly viewRows = computed(() => this._view().rows)

    // While a page is in flight the table keeps its shape: a full page of blank rows, so the grid stays put
    // (blurred, behind the loader) instead of collapsing to an empty box.
    readonly displayRows = computed(() => (this.loading() ? new Array(this.pageSize()).fill({}) : this.viewRows()))

    readonly pagerLength = computed(() => this._view().total)
    readonly pagerIndex = computed(() => this._view().pageIndex)

    readonly lastPageIndex = computed(() =>
        Math.max(0, Math.ceil(this.pagerLength() / Math.max(1, this.pageSize())) - 1))

    readonly rangeLabel = computed(() => {
        const length = this.pagerLength()
        const size = this.pageSize()
        if (!length || !size) return "Nema podataka"
        const first = this.pagerIndex() * size + 1
        const last = Math.min((this.pagerIndex() + 1) * size, length)
        return `Prikazuje se ${first} do ${last} (ukupno ${length} stavki)`
    })

    private readonly _filterInput$ = new Subject<void>()
    private readonly _dialog = inject(MatDialog)
    private readonly _contextService = inject(ContextService)
    private readonly _router = inject(Router)
    private readonly _destroyRef = inject(DestroyRef)
    private _initialised = false

    get stateKey(): string {
        return `${TableComponent._statePrefix}${this._router.url}::${this.entityType()}`
    }

    ngOnInit(): void {
        // Leaving a section discards every table's remembered view; an edit/add round-trip stays on the same
        // route and keeps it. A full reload also starts clean, since _lastRoute only lives in memory.
        if (TableComponent._lastRoute !== this._router.url) {
            TableComponent._clearStoredState()
            TableComponent._lastRoute = this._router.url
        }

        this.pageSize.set(this.rows())
        this.pageIndex.set(this.rows() ? Math.floor(this.first() / this.rows()) : 0)
        this._restoreState()

        if (this.columns().some(col => col.filterType === "cats")) {
            this._contextService.InitCategories()
            this._contextService.getCategoriesSubject()
                ?.pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe(res => this.allCats.set(
                    res.filter(cat => cat.type === this.entityType() && cat.level !== 0 && cat.isActive).map(cat => cat.names.hr),
                ))
        }

        if (this.columns().some(col => col.filterType === "locs")) {
            this._contextService.InitLocations()
            this._contextService.getLocationsSubject()
                ?.pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe(res => this.allLocs.set(res.map(loc => loc.names.hr)))
        }

        this._filterInput$
            .pipe(debounceTime(300), takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                this.pageIndex.set(0)
                this._emit()
            })
    }

    ngAfterViewInit(): void {
        this._initialised = true
        if (this.lazy()) {
            this._emit()
        }
    }

    trackByRow = (index: number, row: any): unknown => row?.["masterId"] ?? index

    // Reads the signal first so OnPush repaints; col.subField is the seed and the cross-component mirror.
    subFieldOf(col: Column): string | undefined {
        return this._subFields()[col.field] ?? col.subField
    }

    sortKey(col: Column): string {
        const sub = this.subFieldOf(col)
        return sub ? `${col.field}.${sub}` : col.field
    }

    // Header labels follow their cells: every body type rendered centered gets a centered header too.
    private static readonly _centeredTypes = new Set([
        "text", "date", "number", "boolean", "button", "modal", "map", "kngEvaluation", "landlordData",
    ])

    isCentered(col: Column): boolean {
        if (col.alignLeft || col.pairWith) return false
        return TableComponent._centeredTypes.has(col.bodyType)
    }

    isResizable(col: Column): boolean {
        return col.bodyType === "langs"
    }

    // Reads the signal first so OnPush repaints - same shape as subFieldOf.
    widthOf(col: Column): number {
        return this._widths()[col.field] ?? DEFAULT_COL_PX[col.bodyType]
    }

    onResizeStart(col: Column): void {
        this._dragStartPx = this.widthOf(col)
    }

    onResizeMove(col: Column, event: CdkDragMove): void {
        const next = Math.min(MAX_COL_PX, Math.max(MIN_COL_PX, this._dragStartPx + event.distance.x))
        this._widths.update(current => ({ ...current, [col.field]: next }))
    }

    optionsFor(col: Column): string[] {
        return col.filterType === "cats" ? this.allCats()
            : col.filterType === "locs" ? this.allLocs()
            : col.filterType === "states" ? this.allStates
            : []
    }

    isMultiFilter(col: Column): boolean {
        return col.filterType === "cats" || col.filterType === "locs" || col.filterType === "states"
    }

    filterValue(col: Column): unknown {
        return this.filters()[col.field]
    }

    hasFilter(col: Column): boolean {
        const value = this.filterValue(col)
        if (Array.isArray(value)) return value.length > 0
        return value !== undefined && value !== null && value !== ""
    }

    isChecked(col: Column, option: string): boolean {
        const value = this.filterValue(col)
        return Array.isArray(value) && value.includes(option)
    }

    toggleOption(col: Column, option: string, checked: boolean): void {
        const current = Array.isArray(this.filterValue(col)) ? [...(this.filterValue(col) as string[])] : []
        const next = checked ? [...current, option] : current.filter(item => item !== option)
        this.onFilter(col, next.length ? next : undefined, true)
    }

    onSort(event: Sort): void {
        this.sortField.set(event.active)
        this.sortDir.set(event.direction === "asc" ? "asc" : "desc")
        this._emit()
    }

    setPageSize(size: number): void {
        if (size === this.pageSize()) return
        const firstRow = this.pagerIndex() * this.pageSize()
        this.pageSize.set(size)
        this.pageIndex.set(Math.floor(firstRow / size))
        this._emit()
    }

    goToPage(index: number): void {
        const clamped = Math.min(Math.max(index, 0), this.lastPageIndex())
        if (clamped === this.pagerIndex()) return
        this.pageIndex.set(clamped)
        this._emit()
    }

    onGlobalFilter(value: string): void {
        this.globalFilter.set(value.trim())
        this._filterInput$.next()
    }

    onFilter(col: Column, value: unknown, immediate: boolean = false): void {
        if (typeof value === "string") value = value.trim()

        this.filters.update(current => {
            const next = { ...current }
            if (value === undefined || value === null || value === "") {
                delete next[col.field]
            } else {
                next[col.field] = value
            }
            return next
        })

        if (immediate) {
            this.pageIndex.set(0)
            this._emit()
        } else {
            this._filterInput$.next()
        }
    }

    clearFilter(col: Column): void {
        this.onFilter(col, null, true)
    }

    setSubField(col: Column, lang: string): void {
        // Pages build their own server filter key off this same Column object (see the _buildColumnFilters
        // in news/benefits/perks/... pages), so the in-place write is a cross-component contract. The signal
        // is what this component reacts to.
        col.subField = lang
        this._subFields.update(current => ({ ...current, [col.field]: lang }))
        this._emit()
    }

    onRefresh(): void {
        this.refresh()?.()
    }

    openPdfFile(fileName: string): void {
        if (!fileName) return

        this._contextService.getStaticPdfFile(fileName).subscribe({
            next: response => {
                const bytes = atob(response.fileBytes)
                const byteNumbers = new Array(bytes.length)
                for (let i = 0; i < bytes.length; i++) {
                    byteNumbers[i] = bytes.charCodeAt(i)
                }
                const byteArray = new Uint8Array(byteNumbers)
                const blob = new Blob([byteArray], { type: "application/pdf" })
                const url = window.URL.createObjectURL(blob)
                window.open(url)
            },
            error: error => {
                console.error("Error downloading file:", error)
            },
        })
    }

    openContracts(row: any): void {
        this._dialog.open(ContractsDialogComponent, {
            data: {
                employee: `${row.name ?? ""} ${row.surname ?? ""}`.trim(),
                rentalAgreement: this._toFileContent(row?.rentalAgreement),
                subrentalAgreement: this._toFileContent(row?.subrentalAgreement),
            },
            width: "56rem",
            maxWidth: "95vw",
        })
    }

    private _toFileContent(value: unknown): FileContent {
        if (!value) {
            return FileContentFactory.create()
        }
        if (typeof value === "string") {
            return FileContentFactory.fromUrl(value)
        }
        if (typeof value === "object") {
            const parsed = value as Partial<FileContent>
            return {
                url: parsed.url ?? "",
                base64: parsed.base64 ?? "",
                fileName: parsed.fileName ?? "",
                contentType: parsed.contentType ?? "",
            }
        }
        return FileContentFactory.create()
    }

    // Emitting is a side effect and must stay out of the computeds: for lazy tables the parent answers by
    // pushing new values() back in, which would re-enter the computation.
    private _emit(): void {
        if (!this._initialised) return

        this._saveState()
        if (this.lazy()) {
            this.lazyLoad.emit({
                first: this.pageIndex() * this.pageSize(),
                rows: this.pageSize(),
                sortField: this.sortField(),
                sortOrder: this.sortDir() === "asc" ? 1 : -1,
                globalFilter: this.globalFilter() || undefined,
                filters: this._toFilterMetadata(),
            })
        }
    }

    private _toFilterMetadata(): Record<string, ColumnFilterMeta> {
        const meta: Record<string, ColumnFilterMeta> = {}
        for (const col of this.orderedColumns()) {
            const raw = this.filterValue(col)
            if (raw === undefined || raw === null || raw === "") continue
            if (Array.isArray(raw) && raw.length === 0) continue

            meta[this.sortKey(col)] = {
                value: col.filterType === "date" ? new Date(raw as string) : raw,
                matchMode: this._matchModeFor(col),
            }
        }
        return meta
    }

    private _matchModeFor(col: Column): string {
        if (col.filterMode) return col.filterMode
        if (col.filterType === "text") return "contains"
        if (col.filterType === "date") return "dateIs"
        if (this.isMultiFilter(col)) return "inArr"
        return "equals"
    }

    private _matchesGlobal(row: any): boolean {
        const needle = this.globalFilter().toLowerCase()
        if (!needle) return true
        return this.orderedColumns().some(col => {
            const value = this._valueAt(row, this.sortKey(col))
            return value !== undefined && value !== null && String(value).toLowerCase().includes(needle)
        })
    }

    private _matchesColumns(row: any): boolean {
        for (const col of this.orderedColumns()) {
            const filter = this.filterValue(col)
            if (filter === undefined || filter === null || filter === "") continue
            if (Array.isArray(filter) && filter.length === 0) continue
            if (!this._matches(this._valueAt(row, this.sortKey(col)), filter, this._matchModeFor(col))) {
                return false
            }
        }
        return true
    }

    private _matches(value: unknown, filter: unknown, matchMode: string): boolean {
        switch (matchMode) {
            case "contains":
                return value !== undefined && value !== null
                    && String(value).toLowerCase().includes(String(filter).toLowerCase())
            case "dateIs": {
                if (!value) return false
                const a = new Date(value as string)
                const b = new Date(filter as string)
                return a.getFullYear() === b.getFullYear()
                    && a.getMonth() === b.getMonth()
                    && a.getDate() === b.getDate()
            }
            case "inArr": {
                const wanted = Array.isArray(filter) ? filter : [filter]
                if (!Array.isArray(value)) return false
                return wanted.every(item => value.includes(item))
            }
            default:
                return String(value) === String(filter)
        }
    }

    private _sortRows(rows: any[]): any[] {
        const field = this.sortField()
        if (!field) return rows
        const order = this.sortDir() === "asc" ? 1 : -1

        return [...rows].sort((rowA, rowB) => {
            const a = this._valueAt(rowA, field)
            const b = this._valueAt(rowB, field)

            if (a == null && b != null) return -1 * order
            if (a != null && b == null) return 1 * order
            if (a == null && b == null) return 0
            if (typeof a === "string" && typeof b === "string") return a.localeCompare(b) * order
            return (a < b ? -1 : a > b ? 1 : 0) * order
        })
    }

    private _valueAt(row: any, field: string): any {
        return field.split(".").reduce((acc, part) => (acc == null ? acc : acc[part]), row)
    }

    private _saveState(): void {
        const subFields: Record<string, string> = {}
        this.orderedColumns().forEach(col => {
            const lang = this.subFieldOf(col)
            if (lang) subFields[col.field] = lang
        })

        const state: StoredState = {
            first: this.pageIndex() * this.pageSize(),
            rows: this.pageSize(),
            sortField: this.sortField(),
            sortOrder: this.sortDir() === "asc" ? 1 : -1,
            global: this.globalFilter(),
            filters: this.filters(),
            subFields,
        }
        sessionStorage.setItem(this.stateKey, JSON.stringify(state))
    }

    private _restoreState(): void {
        const raw = sessionStorage.getItem(this.stateKey)
        if (!raw) return

        let state: StoredState
        try {
            state = JSON.parse(raw)
        } catch {
            sessionStorage.removeItem(this.stateKey)
            return
        }

        this.pageSize.set(state.rows || this.rows())
        this.pageIndex.set(this.pageSize() ? Math.floor((state.first ?? 0) / this.pageSize()) : 0)
        this.sortField.set(state.sortField || this.sortField())
        this.sortDir.set(state.sortOrder === 1 ? "asc" : "desc")
        this.globalFilter.set(state.global ?? "")
        this.filters.set(state.filters ?? {})

        if (state.subFields) {
            this._subFields.set({ ...state.subFields })
            this.orderedColumns().forEach(col => {
                const lang = state.subFields[col.field]
                if (lang) col.subField = lang
            })
        }
    }
}
